import { env } from '@/config/env';
import { PageHeader } from '@/shared/components/PageHeader';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import {
  Award,
  Briefcase,
  CalendarOff,
  ChevronRight,
  ClipboardList,
  Clock,
  FileText,
  History,
  Layers,
  Lock,
  MapPin,
  MapPinned,
  ScrollText,
  Search,
  Settings as SettingsIcon,
  Users,
  Wand2,
  Wrench,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

interface SettingsSubItem {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  route: string;
}

interface SettingsSection {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  items: SettingsSubItem[];
}

const settingsSections: SettingsSection[] = [
  {
    id: 'job-management',
    title: 'Job Management',
    description: 'Manage job categories, task types, and form assignments',
    icon: Briefcase,
    items: [
      {
        id: 'job-categories',
        title: 'Job Categories',
        description: 'View and manage job category types',
        icon: Layers,
        route: '/settings/job-categories',
      },
      {
        id: 'task-types',
        title: 'Task Types',
        description: 'Configure available task types',
        icon: ClipboardList,
        route: '/settings/task-types',
      },
      {
        id: 'category-tasks',
        title: 'Category Tasks',
        description: 'Assign tasks to job categories',
        icon: Briefcase,
        route: '/settings/category-tasks',
      },
      {
        id: 'form-builder',
        title: 'Form Builder',
        description: 'Build and manage forms for tasks',
        icon: FileText,
        route: '/settings/form-builder',
      },
    ],
  },
  {
    id: 'user-workforce',
    title: 'User & Workforce',
    description: 'Manage job titles, disciplines, skills, and leave configuration',
    icon: Users,
    items: [
      {
        id: 'job-titles',
        title: 'Job Titles',
        description: 'Manage job titles for user profiles',
        icon: Briefcase,
        route: '/settings/job-titles',
      },
      {
        id: 'disciplines',
        title: 'Disciplines',
        description: 'Manage trades and disciplines',
        icon: Wrench,
        route: '/settings/disciplines',
      },
      {
        id: 'skills',
        title: 'Skills',
        description: 'Manage skills for the Skills Matrix',
        icon: Award,
        route: '/settings/skills',
      },
      {
        id: 'leave-types',
        title: 'Leave Types',
        description: 'Manage leave types for the Leave Calendar',
        icon: CalendarOff,
        route: '/settings/leave-types',
      },
      {
        id: 'regions',
        title: 'Regions',
        description: 'Group areas into regions for team coverage',
        icon: MapPinned,
        route: '/settings/regions',
      },
      {
        id: 'areas',
        title: 'Areas',
        description: 'Manage areas and the postcodes they cover',
        icon: MapPin,
        route: '/settings/areas',
      },
      {
        id: 'teams',
        title: 'Teams',
        description: 'View and manage teams',
        icon: Users,
        route: '/teams',
      },
      {
        id: 'shifts',
        title: 'Shifts',
        description: 'View and manage shift patterns',
        icon: Clock,
        route: '/shifts',
      },
    ],
  },
  {
    id: 'compliance',
    title: 'Compliance',
    description: 'Regulatory compliance tools',
    icon: ScrollText,
    items: [
      {
        id: 'audit-trail',
        title: 'Audit Trail',
        description: 'Search and export system activity logs',
        icon: History,
        route: '/settings/audit-trail',
      },
    ],
  },
];

export function SystemSettingsView() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState<string[]>([
    'job-management',
    'user-workforce',
    'compliance',
  ]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId) ? prev.filter((id) => id !== sectionId) : [...prev, sectionId],
    );
  };

  const filteredSections = settingsSections
    .map((section) => {
      if (!searchQuery) return section;
      const searchLower = searchQuery.toLowerCase();

      const matchesSection =
        section.title.toLowerCase().includes(searchLower) ||
        section.description.toLowerCase().includes(searchLower);

      const matchingItems = section.items.filter(
        (item) =>
          item.title.toLowerCase().includes(searchLower) ||
          item.description.toLowerCase().includes(searchLower),
      );

      if (matchesSection) return section;
      if (matchingItems.length > 0) return { ...section, items: matchingItems };
      return null;
    })
    .filter(Boolean) as SettingsSection[];

  return (
    <div className="h-full flex flex-col bg-background overflow-hidden">
      <PageHeader
        title="Settings"
        subtitle="Manage system configuration and preferences"
        icon={<SettingsIcon className="w-7 h-7" style={{ color: '#E84A2B' }} />}
      />

      <div className="flex-1 overflow-y-auto px-6 pb-6 pt-2">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search settings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-11 pl-10 pr-4 bg-input-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          {/* Settings Sections */}
          <div className="space-y-3">
            {filteredSections.map((section) => {
              const SectionIcon = section.icon;
              const isExpanded = expandedSections.includes(section.id);

              return (
                <div
                  key={section.id}
                  className="bg-card border border-border rounded-lg overflow-hidden"
                >
                  {/* Section Header */}
                  <div className="px-5 py-4 flex items-center justify-between hover:bg-accent/50 transition-colors">
                    <button
                      onClick={() => toggleSection(section.id)}
                      className="flex items-center gap-3 flex-1"
                    >
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: 'rgba(232, 74, 43, 0.1)' }}
                      >
                        <SectionIcon className="w-5 h-5" style={{ color: '#E84A2B' }} />
                      </div>
                      <div className="text-left">
                        <span className="text-base font-medium text-foreground block">
                          {section.title}
                        </span>
                        <span className="text-xs text-muted-foreground">{section.description}</span>
                      </div>
                    </button>
                    <div className="flex items-center gap-2">
                      {section.id === 'job-management' && (
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/settings/job-management');
                          }}
                          className="bg-[#E84A2B] hover:bg-[#d43d20] text-white"
                        >
                          <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                          Setup Wizard
                        </Button>
                      )}
                      <button type="button" onClick={() => toggleSection(section.id)}>
                        <ChevronRight
                          className={`w-5 h-5 text-muted-foreground transition-transform ${
                            isExpanded ? 'rotate-90' : ''
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Sub-items as cards */}
                  {isExpanded && (
                    <div className="border-t border-border bg-muted/30 p-5">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {section.items.map((item) => {
                          const ItemIcon = item.icon;
                          return (
                            <Card
                              key={item.id}
                              onClick={() => navigate(item.route)}
                              className="p-5 cursor-pointer hover:shadow-md hover:border-[#E84A2B]/30 transition-all group"
                            >
                              <div className="flex items-start gap-4">
                                <div
                                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                                  style={{ backgroundColor: 'rgba(232, 74, 43, 0.1)' }}
                                >
                                  <ItemIcon className="w-5 h-5" style={{ color: '#E84A2B' }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-sm font-semibold text-foreground group-hover:text-[#E84A2B] transition-colors">
                                    {item.title}
                                  </h3>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {item.description}
                                  </p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-[#E84A2B] transition-colors flex-shrink-0 mt-0.5" />
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Coming Soon — dev only */}
          {env.isDevelopment && !searchQuery && (
            <div className="bg-card border border-border rounded-lg overflow-hidden opacity-60">
              <div className="px-5 py-4 flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(232, 74, 43, 0.1)' }}
                >
                  <Lock className="w-5 h-5" style={{ color: '#E84A2B' }} />
                </div>
                <div className="text-left">
                  <span className="text-base font-medium text-foreground block">Coming Soon</span>
                  <span className="text-xs text-muted-foreground">
                    Planned features for future releases
                  </span>
                </div>
              </div>
              <div className="border-t border-border bg-muted/30 p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    {
                      title: 'Scheduling Rules',
                      description: 'Auto-assignment and capacity rules',
                    },
                    {
                      title: 'Notification Settings',
                      description: 'Configure email and push notifications',
                    },
                    {
                      title: 'Workflow Configuration',
                      description: 'Manage form and task workflows',
                    },
                    {
                      title: 'Data Retention',
                      description: 'Configure archiving and retention policies',
                    },
                  ].map((item) => (
                    <Card key={item.title} className="p-5 pointer-events-none opacity-70">
                      <div className="flex items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-foreground">{item.title}</h3>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                              Planned
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* No Results */}
          {filteredSections.length === 0 && (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-foreground mb-2">No results found</h3>
              <p className="text-sm text-muted-foreground">Try adjusting your search query</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
